import React, { useEffect, useState, useCallback } from 'react';
import { Inbox, Loader2, ChevronLeft, ChevronRight, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/adminApi';
import { EmptyState } from '@/components/admin/EmptyState';

const TOPIC_STYLE = {
  support: 'bg-brand/15 text-brand',
  sales: 'bg-success/15 text-success',
  provider: 'bg-yellow-400/15 text-yellow-400',
  general: 'bg-white/10 text-white/60',
};

export default function AdminContacts() {
  const [data, setData] = useState({ contacts: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchContacts = useCallback(() => {
    setLoading(true);
    api.get('/admin/contacts', { params: { page, limit: 50 } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const removeContact = async (c) => {
    if (!window.confirm(`Delete this message from ${c.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      toast.success('Message deleted');
      fetchContacts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Contact Inbox</h1>
        <p className="mt-1 text-sm text-white/50">{data.total} real messages submitted through your contact form.</p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : data.total === 0 ? (
        <EmptyState icon={Inbox} title="Inbox is empty" description="Messages sent through your contact form will appear here in real time, so you can respond to leads and support requests." />
      ) : (
        <>
          <div className="space-y-3">
            {data.contacts.map((c) => (
              <div key={c.id} data-testid="admin-contact-row" className="rounded-2xl border border-white/10 bg-surface/70 p-5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 font-display font-bold text-white/70">{(c.name || '?').charAt(0).toUpperCase()}</span>
                    <div>
                      <p className="font-medium text-white">{c.name}</p>
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs text-white/50 hover:text-brand"><Mail className="h-3 w-3" /> {c.email}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.topic && <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${TOPIC_STYLE[c.topic] || TOPIC_STYLE.general}`}>{c.topic}</span>}
                    <span className="text-xs text-white/40">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-white/70">{c.message}</p>
                <a href={`mailto:${c.email}?subject=Re: your Copy.Click enquiry`} className="mt-3 inline-block text-xs font-semibold text-brand hover:underline">Reply →</a>
                <button onClick={() => removeContact(c)} data-testid="admin-contact-delete" className="mt-3 ml-4 inline-flex items-center gap-1 text-xs font-semibold text-white/40 hover:text-destructive">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between text-sm text-white/50">
            <span>Page {data.page} of {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-white/10 p-1.5 hover:bg-white/5 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-white/10 p-1.5 hover:bg-white/5 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
